import { z } from 'zod';

import {
  LensIdSchema,
  NonEmptyStringSchema,
  PropertyIdSchema,
  QidSchema,
  StatementIdSchema,
  StatementRankSchema,
} from './primitives.js';
import { ReferenceEvidenceSchema } from './source.js';
import { QualifiedTimeSchema, relationshipTimeOrderIsValid } from './time.js';

export const RelationIdSchema = z.enum([
  'spouse',
  'unmarried-partner',
  'parent-child',
  'stepparent',
  'sibling',
  'relative',
  'godparent',
  'professional-collaborator',
  'student-of',
  'doctoral-advisor',
  'appointed-by',
  'creative-collaborator',
  'organizational-collaborator',
]);

export type RelationId = z.infer<typeof RelationIdSchema>;

export const EdgeContextSchema = z.object({
  id: QidSchema.optional(),
  label: NonEmptyStringSchema,
  url: z.url().optional(),
  roles: z.array(NonEmptyStringSchema).optional(),
  participantRoles: z
    .array(
      z.object({
        personId: QidSchema,
        roles: z.array(NonEmptyStringSchema).min(1),
      }),
    )
    .min(2)
    .optional(),
});

export type EdgeContext = z.infer<typeof EdgeContextSchema>;

export const EdgeDirectionSchema = z.object({
  subject: QidSchema,
  object: QidSchema,
  subjectRole: NonEmptyStringSchema,
  objectRole: NonEmptyStringSchema,
});

export type EdgeDirection = z.infer<typeof EdgeDirectionSchema>;

export const EdgeEvidenceGroupSchema = z.object({
  endpointId: QidSchema,
  statementIds: z.array(StatementIdSchema).min(1),
  references: z.array(ReferenceEvidenceSchema),
});

export type EdgeEvidenceGroup = z.infer<typeof EdgeEvidenceGroupSchema>;

export const SupplementalPublicationStatusSchema = z.enum(['reported', 'rumored', 'disputed']);

export type SupplementalPublicationStatus = z.infer<typeof SupplementalPublicationStatusSchema>;

const EdgeCandidateBaseSchema = z.object({
  id: NonEmptyStringSchema,
  source: QidSchema,
  target: QidSchema,
  lens: LensIdSchema,
  relation: RelationIdSchema,
  propertyId: PropertyIdSchema,
  directed: z.boolean(),
  direction: EdgeDirectionSchema.optional(),
  start: QualifiedTimeSchema.optional(),
  end: QualifiedTimeSchema.optional(),
  context: EdgeContextSchema.optional(),
  statementIds: z.array(StatementIdSchema).min(1),
  statementRank: StatementRankSchema,
  references: z.array(ReferenceEvidenceSchema),
  evidenceGroups: z.array(EdgeEvidenceGroupSchema).min(2).optional(),
  publicationStatusHint: SupplementalPublicationStatusSchema.optional(),
});

function validateTemporalOrder(
  value: {
    start?: z.infer<typeof QualifiedTimeSchema> | undefined;
    end?: z.infer<typeof QualifiedTimeSchema> | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (!relationshipTimeOrderIsValid(value.start, value.end)) {
    context.addIssue({
      code: 'custom',
      path: ['end'],
      message: 'Relationship end cannot be earlier than its start.',
    });
  }
}

export const EdgeCandidateSchema = EdgeCandidateBaseSchema.superRefine(validateTemporalOrder);

export type EdgeCandidate = z.infer<typeof EdgeCandidateSchema>;

/**
 * Public claim provenance/status. `documented` remains readable for historical
 * fixtures and artifacts; policy-v4 Wikidata statements publish as `wikidata`.
 * The remaining values are reserved for attributed supplemental claims.
 */
export const EdgePublicationStatusSchema = z.enum([
  'documented',
  'wikidata',
  'reported',
  'rumored',
  'disputed',
]);

export type EdgePublicationStatus = z.infer<typeof EdgePublicationStatusSchema>;

export const AcceptedEdgeSchema = EdgeCandidateBaseSchema.omit({
  references: true,
  evidenceGroups: true,
  statementRank: true,
  publicationStatusHint: true,
})
  .extend({
    status: EdgePublicationStatusSchema,
    sourceIds: z.array(NonEmptyStringSchema).min(1),
    acceptanceReasons: z.array(NonEmptyStringSchema).min(1),
  })
  .superRefine(validateTemporalOrder);

export type AcceptedEdge = z.infer<typeof AcceptedEdgeSchema>;

export const EdgeRejectionReasonSchema = z.enum([
  'deprecated-statement',
  'invalid-endpoint',
  'endpoint-not-selected',
  'missing-kinship-qualifier',
  'missing-position-context',
  'missing-overlap-evidence',
  'missing-substantial-role',
  'incomplete-derived-evidence',
  'living-person-evidence-required',
  'sensitive-personal-evidence-required',
  'minor-personal-edge',
  'ambiguous-semantics',
  'unclassified-source',
  'density-cap',
  'duplicate',
]);

export type EdgeRejectionReason = z.infer<typeof EdgeRejectionReasonSchema>;

export const RejectedEdgeSchema = z.object({
  candidate: EdgeCandidateSchema,
  reasons: z.array(EdgeRejectionReasonSchema).min(1),
  detail: z.array(NonEmptyStringSchema),
});

export type RejectedEdge = z.infer<typeof RejectedEdgeSchema>;
